import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notify } from '@nimling/components-nimtech'
import type { Source, SourceState } from '@/types'
import * as api from '@/lib/api'

export const useSourcesStore = defineStore('sources', () => {
  const isLoading = ref(false)
  const loaded = ref(false)
  const sources = ref<Source[]>([])

  const getSources = async (): Promise<Source[]> => {
    isLoading.value = true
    try {
      const rows = await api.getSources()
      sources.value = rows
      loaded.value = true
      return rows
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load sources')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getSource = (id: string | null | undefined): Source | undefined => {
    if (!id) return undefined
    return sources.value.find(s => s.id === id)
  }

  const secrets = new Map<string, string>()

  const getSecret = (id: string): string => {
    const held = secrets.get(id) ?? ''
    secrets.delete(id)
    return held
  }

  const putSource = async (input: Parameters<typeof api.putSource>[0]): Promise<Source & { secret?: string }> => {
    const updated = await api.putSource(input)
    if (updated.secret) secrets.set(updated.id, updated.secret)
    const idx = sources.value.findIndex(x => x.id === updated.id)
    if (idx >= 0) {
      const next = [...sources.value]
      next[idx] = updated
      sources.value = next
    } else {
      sources.value = [...sources.value, updated]
    }
    notify('success', `Source ${updated.name} saved`)
    return updated
  }

  const deleteSource = async (id: string): Promise<void> => {
    await api.deleteSource(id)
    sources.value = sources.value.filter(x => x.id !== id)
    notify('info', `Source ${id} removed`)
  }

  const postSourceCopy = async (id: string): Promise<Source | null> => {
    try {
      const created = await api.postSourceCopy(id)
      sources.value = [...sources.value, created]
      notify('success', `Source ${created.name} cloned`)
      return created
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to clone source')
      return null
    }
  }

  const postSourceValidate = async (id: string): Promise<SourceState | null> => {
    try {
      const { state } = await api.postSourceValidate(id)
      const idx = sources.value.findIndex(x => x.id === id)
      if (idx >= 0) {
        const next = [...sources.value]
        next[idx] = { ...next[idx], state }
        sources.value = next
      }
      return state
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Source validation failed')
      return null
    }
  }

  return { isLoading, loaded, sources, getSources, getSource, getSecret, putSource, deleteSource, postSourceCopy, postSourceValidate }
})
